// T3-B sponsored motif (PR 4, 2026-05-07):
// sponsor の create / edit を担う Sheet (slide-over) UI。
//
// Spec: design/sponsored-motif-spec.md §6.2 (campaign 入稿フォーム — sponsor 選択時に新規作成可能)

import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Textarea } from '@/components/common/Textarea';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

const sponsorSchema = yup.object({
    name: yup
        .string()
        .required('名称を入力してください')
        .max(200, '200 文字以下を入力してください'),
    contactEmail: yup
        .string()
        .required('連絡先メールを入力してください')
        .email('正しいメールアドレスを入力してください')
        .max(200),
    contactName: yup
        .string()
        .nullable()
        .max(100)
        .transform((v, o) => (o === '' ? null : v)),
    logoUrl: yup
        .string()
        .nullable()
        .max(500)
        .transform((v, o) => (o === '' ? null : v))
        .url('URL を入力してください'),
    industry: yup
        .string()
        .nullable()
        .max(50)
        .transform((v, o) => (o === '' ? null : v)),
    notes: yup
        .string()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
});

type SponsorFormValues = yup.InferType<typeof sponsorSchema>;

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    mode: 'create' | 'edit',
    sponsor: any | null,
    onSuccess?: () => void
}

export default observer(function SponsorFormSheet({ open, onOpenChange, mode, sponsor, onSuccess }: Props) {
    const {
        sponsorStore: { create: createSponsor, update: updateSponsor }
    } = useStore();

    const form = useForm<SponsorFormValues>({
        resolver: yupResolver(sponsorSchema),
        mode: 'onSubmit',
        defaultValues: {
            name: '',
            contactEmail: '',
            contactName: '',
            logoUrl: '',
            industry: '',
            notes: ''
        }
    });

    useEffect(() => {
        if (open) {
            form.reset(
                mode === 'edit' && sponsor ?
                    {
                        name: sponsor.name ?? '',
                        contactEmail: sponsor.contact_email ?? '',
                        contactName: sponsor.contact_name ?? '',
                        logoUrl: sponsor.logo_url ?? '',
                        industry: sponsor.industry ?? '',
                        notes: sponsor.notes ?? ''
                    } :
                    {
                        name: '',
                        contactEmail: '',
                        contactName: '',
                        logoUrl: '',
                        industry: '',
                        notes: ''
                    }
            );
        }
    }, [open, mode, sponsor]);

    const onSubmit = async (data: SponsorFormValues) => {
        const payload = {
            name: data.name,
            contact_email: data.contactEmail,
            contact_name: data.contactName ?? null,
            logo_url: data.logoUrl ?? null,
            industry: data.industry ?? null,
            notes: data.notes ?? null
        };
        const res = mode === 'create' ?
            await flowResult(createSponsor(payload)) :
            await flowResult(updateSponsor({ id: sponsor.id, data: payload }));

        if (res?.ok) {
            toastify('success', mode === 'create' ? '作成しました' : '更新しました');
            onOpenChange(false);
            onSuccess?.();
        } else {
            toastify('error', res?.message || '保存に失敗しました');
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[600px] sm:max-w-[600px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>{mode === 'create' ? 'スポンサー新規作成' : 'スポンサー編集'}</SheetTitle>
                    <SheetDescription>
                        広告主組織の基本情報を登録します。
                    </SheetDescription>
                </SheetHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4 py-4'>
                        <FormField
                            control={form.control}
                            name='name'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        名称
                                        <span className='text-red-500'>*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder='例: オリオンビール株式会社' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='contactEmail'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        連絡先メール
                                        <span className='text-red-500'>*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <Input type='email' {...field} placeholder='例: contact@example.com' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='contactName'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>担当者名</FormLabel>
                                    <FormControl>
                                        <Input {...field} value={field.value ?? ''} placeholder='例: 山田 太郎' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='logoUrl'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>ロゴ URL</FormLabel>
                                    <FormControl>
                                        <Input {...field} value={field.value ?? ''} placeholder='https://...' />
                                    </FormControl>
                                    <FormMessage />
                                    <p className='text-xs text-gray-500 mt-1'>
                                        ※ 現在は保存のみで enduser 画面には表示されません (将来対応予定)。
                                    </p>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='industry'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>業界</FormLabel>
                                    <FormControl>
                                        <Input {...field} value={field.value ?? ''} placeholder='例: beverage / tobacco / financial / medical / 観光' />
                                    </FormControl>
                                    <FormMessage />
                                    <p className='text-xs text-gray-500 mt-1'>
                                        業種の記録用タグ (任意)。※ 現在は保存のみで、自動の業界規制チェック / 集計フィルタは未実装。
                                    </p>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='notes'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>備考</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} value={field.value ?? ''} rows={3} placeholder='契約条件 / 特記事項など' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <SheetFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                キャンセル
                            </Button>
                            <Button type='submit' variant='active'>
                                {mode === 'create' ? '作成' : '更新'}
                            </Button>
                        </SheetFooter>
                    </form>
                </Form>
            </SheetContent>
        </Sheet>
    );
});
